/* eslint-disable no-undef */
import {
  computed,
  useRoute,
  useRouter,
  useStore,
  watch
} from '@nuxtjs/composition-api'
import {
  catchError,
  concatMap,
  filter,
  from,
  isObservable,
  of,
  tap
} from 'rxjs'
import { checkAxiosData } from 'fe-common'
import { handleMessages, refreshToken } from '../modules/client/utilities'
import useAuthModal from '../composables/useAuthModal'
import useComp from './useComp'
import useErrorHandler from './useErrorHandler'

export default function usePlayGame(context) {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()

  const { linkToOpenAuthModal } = useAuthModal(context)
  const { errorHandler$ } = useErrorHandler(context)
  const { compPlayGame, webEnv } = useComp()
  const templateId = computed(() => process.env.TEMPLATE_ID)

  const isMobile = computed(() => store.state.isMobile)
  const embedUrl = computed(() => store.state.gameModule.embedUrl)

  function play(playParams, beforeAction, afterAction) {
    if (!store.state.authModule.user) {
      store.commit('authModule/LOGIN_MODAL', {
        show: true,
        target: route.value.path
      })
      if (['18', '21', '22', '25'].includes(templateId.value)) {
        linkToOpenAuthModal('login')
      }
      return
    }

    if (
      Cookies.get('notShowGameTransferModalToday') &&
      !!Cookies.get('notShowGameTransferModalToday')
    ) {
      return directPlay(playParams, false, beforeAction, afterAction)
    }

    context.root.$modal.show('financialServiceTransferModal', {
      playParams,
      beforeAction,
      afterAction
    })
  }

  function playTry(playParams, beforeAction, afterAction) {
    directPlay(playParams, true, beforeAction, afterAction)
  }

  function directPlay(
    playParams,
    isTryGame = false,
    beforeAction = () => from(Promise.resolve()),
    afterAction = () => from(Promise.resolve())
  ) {
    let playOpenType = playParams.playOpenType || getPlayOpenType(playParams)
    // GH 無法做 window.open
    if (['RWB', 'GH', 'DT', 'GS'].includes(playParams.platform)) {
      playOpenType = 'internal'
    }
    if (playOpenType === 'internal') {
      store.commit('gameModule/EMBED_URL', null)
      store.commit('gameModule/EMBED_HTML', null)
    }

    let w // for play external use
    if (
      playOpenType === 'external' &&
      !store.state.isAPP &&
      !store.state.isDesktop
    ) {
      w = window.open()
      ;(w || {}).opener = null
    }

    const _beforeAction = beforeAction(w)
    ;(isObservable(_beforeAction) ? _beforeAction : from(_beforeAction))
      .pipe(
        tap(() => store.dispatch('startLoading')),
        concatMap(() =>
          from(
            store.dispatch('gameModule/' + (!isTryGame ? 'play' : 'playTry'), {
              ...playParams
            })
          )
        ),
        tap(() => store.dispatch('completeLoading')),
        tap(data => {
          if (
            !checkAxiosData(data, {
              handleMessages: handleMessages({
                store
              })
            })
          ) {
            if (w) w.close()
          }
        }),
        filter(data =>
          checkAxiosData(data, {
            handleMessages: handleMessages({
              store
            })
          })
        ),
        concatMap(data => of({ playOpenType, data, w })),
        tap(playResponse => send(playParams, playResponse)),
        filter(playResponse => !playRWB(playParams, playResponse)),
        // filter(
        //   playResponse => !(!isTryGame && playPT(playParams, playResponse))
        // ),
        filter(playResponse => !playEVO(playParams, playResponse)),
        filter(playResponse => !playGH(playParams, playResponse)),
        filter(playResponse => !playDT(playParams, playResponse)),
        filter(playResponse => !playGS(playParams, playResponse)),
        filter(playResponse => !playPG(playParams, playResponse)),
        filter(playResponse => !playULTRAPLAY(playParams, playResponse)),
        tap(playResponse => playCommon(playParams, playResponse)),
        concatMap(() => {
          const _afterAction = afterAction()
          return isObservable(_afterAction) ? _afterAction : from(_afterAction)
        }),
        catchError(err => {
          store.dispatch('completeLoading').then()
          if (w) {
            w.close()
          }
          return errorHandler$(err)
        })
      )
      .subscribe()
  }

  function send(playParams, { playOpenType, w }) {
    if (webEnv.value === 'prod' || !store.state.websocketModule.websocket$)
      return
    store
      .dispatch('websocketModule/sendInterval', {
        platform: playParams.platform,
        timestamp: new Date().getTime()
      })
      .then()
    if (playOpenType === 'internal') {
      watch(embedUrl, newVal => {
        if (!newVal) {
          store.dispatch('websocketModule/clearSendInterval').then()
        }
      })
    } else {
      const windowCloseIntervalId = setInterval(() => {
        if (w.closed) {
          store.dispatch('websocketModule/clearSendInterval').then()
          clearInterval(windowCloseIntervalId)
        }
      }, 500)
    }
  }

  function playRWB(playParams, playResponse) {
    if (
      playParams.platform === 'RWB' &&
      validPlayResponseField(playResponse, 'config')
    ) {
      const { data } = playResponse
      localStorage.setItem('rwbConfig', JSON.stringify(data.data.config))

      store.commit('gameModule/EMBED_URL', '/game/rwb.html')
      return true
    }
    return false
  }

  // eslint-disable-next-line no-unused-vars
  function playPT(playParams, playResponse) {
    if (
      playParams.platform === 'PT' &&
      validPlayResponseField(playResponse, 'config')
    ) {
      const { playOpenType, data, w } = playResponse
      if (playOpenType === 'internal') {
        const config = data.data.config

        const hasProxyPlatforms = process.env.HAS_PROXY_PLATFORMS.replace(
          /\s/g,
          ''
        )
          .split(',')
          .filter(Boolean)
        if (hasProxyPlatforms.includes('PT')) {
          const formBody = `
            <input type="hidden" name="scriptSrc" value="${config.scriptSrc}" />
            <input type="hidden" name="username" value="${config.username}" />
            <input type="hidden" name="password" value="${config.password}" />
            <input type="hidden" name="url" value="${config.url}" />
            <input type="hidden" name="systemId" value="${config.systemId ||
              976}" />
            <input type="hidden" name="Authorization" value="${
              store.state.authModule.token
            }" />
          `
          const body = `
                <body>
                <form method="get" action="${process.env.WEB_PROXY_URL +
                  '/api/apiProxy/game/' +
                  (!isMobile.value ? 'pt' : 'pth5')}">
                  ${formBody}
                </form>
                </body>
              `
          console.log('body:', body)

          store.commit(
            'gameModule/EMBED_HTML',
            `<html><head></head>${body}</html>`
          )

          const query = {
            platform: playParams.platform
          }
          if (playParams.bgImageUrl) {
            query.bgImageUrl = encodeURIComponent(playParams.bgImageUrl)
          }
          router.push(
            {
              path: '/play-game',
              query
            },
            f => f,
            f => f
          )
        } else {
          localStorage.setItem('GAME_PT_SCRIPT_SRC', config.scriptSrc)
          localStorage.setItem('GAME_PT_USER_NAME', config.username)
          localStorage.setItem('GAME_PT_PASSWORD', config.password)
          localStorage.setItem('GAME_PT_URL', config.url)
          localStorage.setItem('GAME_PT_SYSTEM_ID', config.systemId || 976)

          localStorage.setItem('SENTRY_DSN', process.env.SENTRY_DSN)
          localStorage.setItem('WEB_ENV', process.env.WEB_ENV)
          localStorage.setItem('COMP_ID', process.env.COMP_ID)

          const location = `/game/${!isMobile.value ? 'pt' : 'pt-h5'}.html`

          store.commit('gameModule/EMBED_URL', location)

          const query = {
            platform: playParams.platform
          }
          if (playParams.bgImageUrl) {
            query.bgImageUrl = encodeURIComponent(playParams.bgImageUrl)
          }
          router.push(
            {
              path: '/play-game',
              query
            },
            f => f,
            f => f
          )
        }
      } else {
        const config = data.data.config

        const hasProxyPlatforms = process.env.HAS_PROXY_PLATFORMS.replace(
          /\s/g,
          ''
        )
          .split(',')
          .filter(Boolean)
        if (hasProxyPlatforms.includes('PT')) {
          const formBody = `
            <input type="hidden" name="scriptSrc" value="${config.scriptSrc}" />
            <input type="hidden" name="username" value="${config.username}" />
            <input type="hidden" name="password" value="${config.password}" />
            <input type="hidden" name="url" value="${config.url}" />
            <input type="hidden" name="systemId" value="${config.systemId ||
              976}" />
            <input type="hidden" name="Authorization" value="${
              store.state.authModule.token
            }" />
          `
          const body = `
                <body>
                <form method="get" action="${process.env.WEB_PROXY_URL +
                  '/api/apiProxy/game/' +
                  (!isMobile.value ? 'pt' : 'pth5')}">
                  ${formBody}
                </form>
                </body>
              `
          console.log('body:', body)
          if (store.state.isAPP) {
            document.write(`<html><head></head>${body}</html>`)
            document.querySelector('form').submit()
          } else {
            w.document.write(`<html><head></head>${body}</html>`)
            w.document.querySelector('form').submit()
          }
        } else {
          localStorage.setItem('GAME_PT_SCRIPT_SRC', config.scriptSrc)
          localStorage.setItem('GAME_PT_USER_NAME', config.username)
          localStorage.setItem('GAME_PT_PASSWORD', config.password)
          localStorage.setItem('GAME_PT_URL', config.url)
          localStorage.setItem('GAME_PT_SYSTEM_ID', config.systemId || 976)

          localStorage.setItem('SENTRY_DSN', process.env.SENTRY_DSN)
          localStorage.setItem('WEB_ENV', process.env.WEB_ENV)
          localStorage.setItem('COMP_ID', process.env.COMP_ID)

          const location = `/game/${!isMobile.value ? 'pt' : 'pt-h5'}.html`

          if (store.state.isAPP) {
            document.location = location
          } else if (store.state.isDesktop) {
            window.open(location, '_blank', 'noopener')
            // listeningToRefreshToken(w)
          } else {
            ;(w || document).location = location
            // listeningToRefreshToken(w)
          }
        }
      }
      return true
    }
    return false
  }

  function playEVO(playParams, playResponse) {
    if (
      playParams.platform === 'EVO' &&
      validPlayResponseField(playResponse, 'config')
    ) {
      const { playOpenType, data, w } = playResponse
      if (playOpenType === 'internal') {
        const config = data.data.config
        const location =
          config.evolution_games_domain + (config.entry || config.entryEmbedded)

        store.commit('gameModule/EMBED_URL', location)

        const query = {
          platform: playParams.platform
        }
        if (playParams.bgImageUrl) {
          query.bgImageUrl = encodeURIComponent(playParams.bgImageUrl)
        }
        router.push(
          {
            path: '/play-game',
            query
          },
          f => f,
          f => f
        )
      } else {
        const config = data.data.config
        let location =
          config.evolution_games_domain + (config.entry || config.entryEmbedded)

        if (isMobile.value) {
          localStorage.setItem('evoConfig', JSON.stringify(config))

          // todo: 2021/10/19 mars 說不知道 sk1 要搞多久
          // const subdomain = !['sk1'].includes(compId.value)
          //   ? 'evogame.'
          //   : ''
          // todo: 2021/10/19 mars 説全部先拿掉 subdomain 設定
          const subdomain = ''
          location =
            'https://' + subdomain + store.state.domain + '/game/evo.html'
        }

        if (store.state.isAPP) {
          document.location = location
        } else if (store.state.isDesktop) {
          window.open(location, '_blank', 'noopener')
          // listeningToRefreshToken(w)
        } else {
          ;(w || document).location = location
          // listeningToRefreshToken(w)
        }
      }
      return true
    }
    return false
  }

  function playGH(playParams, playResponse) {
    if (!isMobile.value && process.env.COMP_ID === 'kb') return false // kb 的 pc 維持原狀
    if (
      playParams.platform === 'GH' &&
      validPlayResponseField(playResponse, 'url')
    ) {
      const { data } = playResponse
      location.href = data.data.url
      return true
    }
    return false
  }

  function playDT(playParams, playResponse) {
    if (
      playParams.platform === 'DT' &&
      validPlayResponseField(playResponse, 'config')
    ) {
      const { data } = playResponse
      localStorage.setItem('dtConfig', JSON.stringify(data.data.config))

      router.push({
        path: '/sportsbook',
        query: {
          view: 'EuropeanView'
        }
      })

      return true
    }
    return false
  }

  function playGS(playParams, playResponse) {
    if (
      playParams.platform === 'GS' &&
      validPlayResponseField(playResponse, 'url')
    ) {
      const { data } = playResponse
      store.commit('gameModule/EMBED_URL', data.data.url)
      if (isMobile.value) {
        const query = {
          platform: playParams.platform
        }
        if (playParams.bgImageUrl) {
          query.bgImageUrl = encodeURIComponent(playParams.bgImageUrl)
        }
        router.push({
          path: '/play-game',
          query
        })
      }
      return true
    }
    return false
  }

  function playULTRAPLAY(playParams, playResponse) {
    const { playOpenType } = playResponse
    if (
      playOpenType === 'internal' &&
      playParams.platform === 'ULTRAPLAY' &&
      validPlayResponseField(playResponse, 'url')
    ) {
      const { data } = playResponse
      store.commit('gameModule/EMBED_URL', data.data.url)
      return true
    }
    return false
  }

  // eslint-disable-next-line no-unused-vars
  function playPG(playParams, playResponse) {
    if (
      playParams.platform === 'PG' &&
      validPlayResponseField(playResponse, 'config')
    ) {
      const { data, w } = playResponse
      const config = data.data.config

      if (w) {
        w.document.write(config.html)
      } else {
        document.write(config.html)
      }

      return true
    }
    return false
  }

  function playCommon(playParams, playResponse) {
    if (!validPlayResponseField(playResponse, 'url')) return

    const { playOpenType, data, w } = playResponse
    if (playOpenType === 'internal') {
      store.commit('gameModule/EMBED_URL', data.data.url)
      if (['ESPORTSBULL'].includes(playParams.platform)) {
        return
      }
      const query = {
        platform: playParams.platform
      }
      if (playParams.bgImageUrl) {
        query.bgImageUrl = encodeURIComponent(playParams.bgImageUrl)
      }
      router.push({
        path: '/play-game',
        query
      })
    } else if (store.state.isAPP) {
      document.location = data.data.url
    } else if (store.state.isDesktop) {
      window.open(data.data.url, '_blank', 'noopener')
      // listeningToRefreshToken(w)
    } else {
      ;(w || document).location = data.data.url
      // listeningToRefreshToken(w)
    }
  }

  function validPlayResponseField({ data }, fieldName) {
    if (
      !data.data[fieldName] ||
      Object.keys(data.data[fieldName]).length === 0
    ) {
      throw new Error(`The play response must have a ${fieldName} field.`)
    }
    return true
  }

  /**
   * 取得該平台的遊戲是內開 or 外開
   * type: external, internal
   * excludeList: ["<device|*>:<platform|*>:<gameType|*|gameType(,)>"], e.g. ["mobile:CQ9:SLOT,LIVE"], * 代表全部
   * 優先度：excludeList > type
   *
   * @param platform
   * @param gameType
   * @returns {string}
   * @private
   */
  function getPlayOpenType({ platform, gameType }) {
    const _webEnv = compPlayGame.value.open.hasOwnProperty(webEnv.value)
      ? webEnv.value
      : 'prod'
    const openType = compPlayGame.value.open[_webEnv].type
    const excludeList = compPlayGame.value.open[_webEnv].excludeList
    let result = openType
    if (excludeList) {
      const hasExclude = excludeList.some(excludeItem => {
        const deviceCondition = [
          '*',
          isMobile.value ? 'mobile' : 'pc'
        ].includes(excludeItem.split(':')[0])

        const platformCondition = ['*', platform].includes(
          excludeItem.split(':')[1]
        )

        const gameTypeCondition = excludeItem
          .split(':')[2]
          .split(',')
          .some(excludeGameType => ['*', gameType].includes(excludeGameType))

        return deviceCondition && platformCondition && gameTypeCondition
      })
      if (hasExclude) {
        result = openType === 'external' ? 'internal' : 'external'
      }
    }
    return result
  }

  /**
   * 暫時不用呼叫，因 default.vue 預設會每 20 分鐘刷新 token (20200421)
   * @param opener
   */
  // eslint-disable-next-line no-unused-vars
  function listeningToRefreshToken(opener) {
    const timer = setInterval(() => {
      if (opener.closed) {
        return clearInterval(timer)
      }
      refreshToken({
        ...store,
        rootState: store.state
      })
    }, 60000)
  }

  return {
    play,
    playTry,
    directPlay
  }
}
